<script>
import utils from '@/utils/common';
import tariffApi from '@/api/tariff';

import FormBlock from '@/components/FormBlock.vue';
import BaseButton from '@/components/BaseButton.vue';
import UserDetailTileField from '@/components/user/UserDetailTileField.vue';

export default {
  name: 'UserDetailFinancesTile',
  components: { FormBlock, BaseButton, UserDetailTileField },
  props: {
    value: Object,
    isEditMode: Boolean,
    isAdmin: Boolean,
  },
  data() {
    return {
      tariffs: [],
    };
  },
  mounted() {
    this.fetchTariffs();
  },
  computed: {
    payments() {
      return this.value.payments.filter(p => p.createdAt).filter(p => p.status === 'confirmed');
    },

    lastPaymentAmount() {
      const { length } = this.payments;
      return length ? this.payments[length - 1].amount : null;
    },

    lastPaymentFullDate() {
      const { length } = this.payments;
      return length ? this.payments[length - 1].createdAt : null;
    },

    lastPaymentDateAndMonth() {
      const date = this.lastPaymentFullDate ? new Date(this.lastPaymentFullDate) : null;
      return date ? `${date.getDate()}.${date.getMonth() + 1}` : null;
    },

    lastPaymentYear() {
      const date = this.lastPaymentFullDate ? new Date(this.lastPaymentFullDate) : null;
      return date ? `${date.getFullYear()}` : null;
    },

    nextPaymentDate() {
      return this.value.nextPaymentDate ? new Date(this.value.nextPaymentDate) : null;
    },

    balanceDisplayValue() {
      return Math.round(this.value.accountBalance / 100) || 0;
    },

    affiliateBalanceDisplayValue() {
      return Math.round(this.value.affiliateBalance / 100) || 0;
    },

    daysBeforeBlock() {
      const price = this.value.tariff.pricePerDay;
      if (price > 0) {
        return Math.round(this.value.accountBalance / this.value.tariff.pricePerDay);
      }
      return 0;
    },

    beforeBlockMessage() {
      const price = this.value.tariff.pricePerDay;
      return (this.daysBeforeBlock > 0 && price > 0) ? `Осталось ${this.daysBeforeBlock} дней до блокировки` : 'Бессрочно';
    },

    dateBlockedAt() {
      if (this.value.accountBlocked) {
        return this.value.accountBlockedAt;
      }
      const currentDateObj = new Date();
      currentDateObj.setDate(this.daysBeforeBlock);
      return currentDateObj.toDateString();
    },

    blockedAtDateAndMonth() {
      const date = new Date(this.dateBlockedAt);
      return this.dateBlockedAt ? `${date.getDate()}.${date.getMonth() + 1}` : null;
    },

    blockedAtYear() {
      const date = new Date(this.dateBlockedAt);
      return this.dateBlockedAt ? `${date.getFullYear()}` : null;
    },

    accountBlockedDisplayValue() {
      return (this.daysBeforeBlock || this.value.accountBlocked) ? this.blockedAtDateAndMonth : null;
    },

    accountBlockedAtSubValue() {
      return (this.daysBeforeBlock || this.value.accountBlocked) ? this.blockedAtYear : 'Бессрочно';
    },

    dataTiles() {
      return [
        { title: 'Денег на счёте',       icon: 'cash-bordered',       value: this.value.accountBalance,    displayValue: this.balanceDisplayValue,            subValue: '₽',                               emptyValue: '0' }, // eslint-disable-line
        { title: 'Дата блокировки',      icon: 'clock-bordered',      value: this.value.accountBlockedAt,  displayValue: this.accountBlockedDisplayValue,     subValue: this.accountBlockedAtSubValue,     emptyValue: '–' }, // eslint-disable-line
        { title: 'Последний платёж',     icon: 'check-bordered',      value: this.lastPaymentFullDate,     displayValue: this.lastPaymentDateAndMonth,        subValue: this.lastPaymentYear,              emptyValue: '–' }, // eslint-disable-line
        { title: 'Реферальный баланс',   icon: 'percents-bordered',   value: this.value.affiliateBalance,  displayValue: this.affiliateBalanceDisplayValue,   subValue: '₽',                               emptyValue: '0' }, // eslint-disable-line
      ];
    },

    dynamicFields() {
      return [
        { name: 'tariff',             title: 'Тариф',                value: this.value.tariff['@id'],          displayValue: this.value.tariff.name,              emptyValue: 'не выбран',    displayView: 'horizontal',  editView: 'horizontal', changeType: 'select', hideNotSelected: true, options: this.tariffOptions }, // eslint-disable-line
        { name: 'affiliateBalance',   title: 'Реферальный баланс',   value: this.value.affiliateBalance,       displayValue: this.affiliateBalanceDisplayValue,   emptyValue: '0',            displayView: 'horizontal',  editView: 'horizontal', changeType: 'input', content: 'finance' }, // eslint-disable-line
        { name: 'accountBalance',     title: 'Баланс',               value: this.value.accountBalance,         displayValue: this.balanceDisplayValue,            emptyValue: '0',            displayView: 'horizontal',  editView: 'horizontal', changeType: 'input', content: 'finance' }, // eslint-disable-line
      ];
    },

    staticFields() {
      return [
        { name: 'accountBlockedAt',   title: 'Дата блокировки',     value: this.value.accountBlockedAt,   displayValue: this.value.accountBlockedAt ? utils.convertDateToString(new Date(this.value.accountBlockedAt), 'numeric') : null,   emptyValue: 'не указана',     displayView: 'horizontal' }, // eslint-disable-line
        { name: 'lastPayment',        title: 'Последний платёж',    value: this.lastPaymentAmount,        displayValue: this.lastPaymentAmount ?`${Math.round(this.lastPaymentAmount) / 100} ₽` : null,                                     emptyValue: 'отсутствует',    displayView: 'horizontal' }, // eslint-disable-line
        { name: 'nextPaymentDate',    title: 'Следующий платёж',    value: this.value.nextPayment,        displayValue: this.nextPaymentDate ? utils.convertDateToString(new Date(this.nextPaymentDate), 'numeric') : null,                 emptyValue: 'не указан',      displayView: 'horizontal' }, // eslint-disable-line
      ];
    },

    tariffOptions() {
      return this.tariffs.map(t => ({ label: t.name, value: t['@id'] }));
    },
  },
  methods: {
    async fetchTariffs() {
      const tariffPromise = await tariffApi.fetchList();
      this.tariffs = tariffPromise.items;
    },

    onChange(name, value) {
      const valueToChange = name === 'tariff' ? this.tariffs.find(t => t['@id'] === value) : value;
      if (name === 'accountBalance' || name === 'affiliateBalance') {
        this.$emit('onChange', { [name]: +valueToChange * 100 });
      } else {
        this.$emit('onChange', { [name]: valueToChange });
      }
    },

    topUpBalance() {
      if (this.isAdmin) {
        this.$emit('topUpBalance', 'accountBalance');
      } else {
        this.$emit('goToTariffs', 'tariffs');
      }
    },
  },
};
</script>

<template lang="pug">
FormBlock(mobile)
  .b-user-detail-finances-tile
    ._header Финансы
      BaseButton(v-if="!isEditMode" blue round md @click="topUpBalance")
        ._header-button Пополнить счёт
    ._display-view(v-if="!isEditMode")
      ._tariff-banner
        ._tariff-actions
          ._tariff-title
            ._tariff-actual Действующий тариф
            ._tariff-name {{ value.tariff.name }}
          ._tariff-price-block
            ._tariff-price {{ Math.round((value.tariff.pricePerDay / 100) * 30) }} ₽
            ._tariff-price-perday Месяц | {{ Math.round(value.tariff.pricePerDay / 100) }} ₽/день
          BaseButton(lg blue round @click="$emit('goToTariffs', 'tariffs')") Поменять тариф
      ._account-data-tiles
        ._account-data-tile(v-for="tile in dataTiles" :key="tile.title")
          ._account-data-header
            ._account-data-header-title {{ tile.title }}
            Icon(:name="tile.icon")
          ._account-data-values
            ._account-data-value(v-if="tile.displayValue !== null") {{ tile.displayValue }}
            ._account-data-subvalue(v-if="tile.subValue !== null")  {{ tile.subValue }}
            ._account-data-emptyvalue(v-if="!tile.displayValue && !tile.subValue") {{ tile.emptyValue }}
      ._account-data-notification(v-if="daysBeforeBlock && !value.accountBlocked") {{ beforeBlockMessage }}
    ._edit-view(v-if="isEditMode")
      ._edit-fields
        UserDetailTileField(v-for="field in dynamicFields" :key="field.name" :field="field" :isEditMode="isEditMode" @change="onChange(field.name, $event)")
      ._static-fields
        UserDetailTileField(v-for="field in staticFields" :key="field.name" :field="field")
</template>

<style scoped lang="scss">
.user-detail-finances-tile {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  gap: 30px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 5px;
    border-bottom: rgba(235, 239, 242, 1) 1px solid;
    font-family: Geometria, sans-serif;
    font-size: 18px;
    font-weight: 500;
    line-height: 1.5;
    color: rgba(30, 49, 71, 1);
  }

  &__header-button {
    color: rgb(255, 255, 255);
    font-family: Geometria, sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: 15px;
    letter-spacing: 1px;
    text-align: center;
  }

  &__display-view {
    display: flex;
    flex-direction: column;
    gap: 30px;
  }

  &__tariff-banner {
    width: 100%;
    height: 111px;
    display: flex;
    background: url("/img/sky-banner.svg") center center repeat;
    background-size: cover;
    border-radius: 8px;
  }

  &__tariff-actions {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-evenly;
  }

  &__tariff-title {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  &__tariff-actual {
    font-family: Geometria, sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 1;
    letter-spacing: 0.01em;
    text-align: center;
    color: rgba(255, 255, 255, 1);
  }

  &__tariff-name {
    font-family: Geometria, sans-serif;
    font-size: 32px;
    font-weight: 500;
    line-height: 1;
    letter-spacing: 0.01em;
    text-align: center;
    color: rgba(255, 255, 255, 1);
  }

  &__tariff-price-block {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  &__tariff-price {
    font-family: Geometria, sans-serif;
    font-size: 30px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.01em;
    text-align: center;
    color: rgba(51, 161, 251, 1);
  }

  &__tariff-price-perday  {
    font-family: Geometria, sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 1;
    letter-spacing: 0.01em;
    text-align: left;
    color: rgba(255, 255, 255, 1);
  }

  &__account-data-tiles {
    display: flex;
    gap: 29px;
  }

  &__account-data-tile {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 206px;
    padding: 14px 17px 20px 17px;
    border-radius: 8px;
    background: rgba(248, 249, 252, 1);
  }

  &__account-data-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__account-data-header-title {
    color: rgb(30, 49, 71, 1);
    font-family: Geometria, sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 15px;
    letter-spacing: 1px;
    text-align: left;
  }

  &__account-data-values {
    display: flex;
    align-items: flex-end;
    gap: 7px;
  }

  &__account-data-value {
    color: rgb(30, 49, 71);
    font-family: Geometria, sans-serif;
    font-size: 32px;
    font-weight: 500;
    line-height: 32px;
    letter-spacing: 1px;
    text-align: left;
  }

  &__account-data-subvalue {
    color: rgb(146, 160, 176);
    font-family: Geometria, sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 15px;
    letter-spacing: 1px;
    text-align: left;
  }

  &__account-data-emptyvalue {
    color: rgb(146, 160, 176);
    font-family: Geometria, sans-serif;
    font-size: 32px;
    font-weight: 500;
    line-height: 32px;
    letter-spacing: 1px;
    text-align: left;
  }

  &__account-data-notification {
    padding: 15px 0 12px 22px;
    border-radius: 8px;
    background-color: rgba(255, 224, 204, 1);
    color: rgb(30, 49, 71);
    font-family: Geometria, sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 15px;
    letter-spacing: 0;
    text-align: left;
  }

  &__edit-view {
    display: flex;
    align-items: center;
    gap: 78px;
  }

  &__edit-fields {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 450px;
  }

  &__static-fields {
    display: flex;
    flex-direction: column;
    gap: 40px;
    width: 450px;
  }
}
</style>
